import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GamePlay from './GamePlay';
import ChatUI from "./Chat";
import './styles.css'
import Header from "../Header"
import DisconnectedScreen from './DisconnectedScreen';
import DebateLikertPopup, { type DebateLikertTiming } from './DebateLikertPopup';
import { usePhaseTracking } from '../../hooks/usePageTracking';
import RewardInfoBox from './GamePlay/RewardInfoBox';
import {
  TOTAL_GAME_ROUNDS,
  ROUNDS_PER_CHAT,
  CHAT_DURATION_SECONDS as CHAT_DURATION,
  ROUND_DURATION_SECONDS as ROUND_DURATION,
  DEBATE_TOPIC_TITLE,
  getDebateLikertQuestion,
  CHAT_FIRST,
  isDebateChatPhase,
  debatePhaseTimingFromPayload,
  resolveSyncedTimerSeconds,
} from '../../gameConfig';
import type { UsdEarningsFromServer } from '../../utils/usdEarnings';

/** Round results modal: default vs after the final investment round (bonus context in UI). */
const ROUND_RESULTS_POPUP_MS = 4000;
const ROUND_RESULTS_POPUP_MS_AFTER_ROUND_4 = 5000;

function roundResultsPopupDurationMs(completedRoundIndex: number): number {
  const lastInvestmentRoundIndex = TOTAL_GAME_ROUNDS - 1;
  return completedRoundIndex === lastInvestmentRoundIndex
    ? ROUND_RESULTS_POPUP_MS_AFTER_ROUND_4
    : ROUND_RESULTS_POPUP_MS;
}

interface GameProps {
  currentUserProlificID: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

function parseApiErrorBody(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const err = (body as { error?: unknown }).error;
    if (typeof err === 'string' && err.trim()) return err;
    if (err && typeof err === 'object' && 'message' in err) {
      const msg = (err as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (status === 404) {
    return 'Save endpoint not found. Restart the backend server, then try again.';
  }
  return `Failed to save (${status})`;
}

function Game({ currentUserProlificID }: GameProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentRound, setCurrentRound] = useState<number>(0);
  const [secondsLeft, setSecondsLeft] = useState<number>(CHAT_FIRST ? CHAT_DURATION : ROUND_DURATION);
  const [playerTotalEarnings, setPlayerTotalEarnings] = useState<number>(0);
  const [playerLastRoundEarning, setPlayerLastRoundEarning] = useState<number | null>(null);
  const [partnerLastRoundEarning, setPartnerLastRoundEarning] = useState<number | null>(null);
  const [roundEndMessage, setRoundEndMessage] = useState<string | null>(null);
  const [isProcessingRoundEnd, setIsProcessingRoundEnd] = useState<boolean>(false);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [isChatPeriod, setIsChatPeriod] = useState<boolean>(CHAT_FIRST);
  const [showRoundPopup, setShowRoundPopup] = useState(false);
  /** True while the round-results modal is for the last investment round (then we go to completion). */
  const [roundPopupIsFinalGameRound, setRoundPopupIsFinalGameRound] = useState(false);
  const [lastRoundEarnings, setLastRoundEarnings] = useState<number | null>(null);
  const [lastPartnerEarnings, setLastPartnerEarnings] = useState<number | null>(null);
  const [playerEarningsHistory, setPlayerEarningsHistory] = useState<number[]>([]);
  const [partnerDisconnected, setPartnerDisconnected] = useState(false);
  const [disconnectReward, setDisconnectReward] = useState<number | null>(null);
  const [phaseStartTime, setPhaseStartTime] = useState<number | null>(null); // Kept for reference, but timeRemaining comes from backend
  const [initialTokens, setInitialTokens] = useState<number>(30); // Default to 30, will be updated from backend
  const [usdEarnings, setUsdEarnings] = useState<UsdEarningsFromServer | null>(null);
  const [debateLikertTiming, setDebateLikertTiming] = useState<DebateLikertTiming | null>(null);
  const [preDebateLikertComplete, setPreDebateLikertComplete] = useState(false);
  const [postDebateLikertComplete, setPostDebateLikertComplete] = useState(false);
  const [debateLikertSubmitting, setDebateLikertSubmitting] = useState(false);
  const [debateTopic, setDebateTopic] = useState<string>(DEBATE_TOPIC_TITLE);
  const chatEndQueuedRef = useRef(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const syncPollingRef = useRef<NodeJS.Timeout | null>(null);
  const currentRoundRef = useRef<number>(0);
  const playerTotalEarningsRef = useRef<number>(0);
  const startChatInFlightRef = useRef<boolean>(false);
  const chatRepairAttemptsRef = useRef(0);
  const phaseStartTimeRef = useRef<number | null>(null);
  const handleChatPeriodEndRef = useRef<(() => Promise<void>) | null>(null);
  const handleRoundEndRef = useRef<(() => Promise<void>) | null>(null);
  const isChatPeriodRef = useRef(isChatPeriod);
  const isProcessingRoundEndRef = useRef(isProcessingRoundEnd);
  /** Round index we've already shown the Round Results popup for (so the other party also sees it when they're behind). */
  const roundResultsPopupShownForRef = useRef<number | null>(null);
  const catchUpFetchInProgressRef = useRef<boolean>(false);
  const runChatPeriodEndRef = useRef<(() => Promise<void>) | null>(null);
  const debateLikertTimingRef = useRef<DebateLikertTiming | null>(null);
  /** Tracks the previous chat-phase value so we can detect the chat → round transition. */
  const prevIsChatPeriodRef = useRef(isChatPeriod);

  // Keep refs in sync with state and LOG all changes
  useEffect(() => {
    console.log(`🔄 STATE CHANGE: currentRound ${currentRoundRef.current} → ${currentRound}`);
    currentRoundRef.current = currentRound;
  }, [currentRound]);

  useEffect(() => {
    isChatPeriodRef.current = isChatPeriod;
  }, [isChatPeriod]);

  useEffect(() => {
    isProcessingRoundEndRef.current = isProcessingRoundEnd;
  }, [isProcessingRoundEnd]);

  useEffect(() => {
    debateLikertTimingRef.current = debateLikertTiming;
  }, [debateLikertTiming]);
  
  useEffect(() => {
    console.log(`🔄 STATE CHANGE: isProcessingRoundEnd = ${isProcessingRoundEnd}`);
  }, [isProcessingRoundEnd]);
  
  // (Bonus popup removed) – chat start is initialized via /startChat when entering chat phase.
  
  useEffect(() => {
    phaseStartTimeRef.current = phaseStartTime;
    console.log(`⏰ SERVER TIME: Phase start time = ${phaseStartTime ? new Date(phaseStartTime).toISOString() : 'null'}`);
  }, [phaseStartTime]);

  const preDebateLikertCompleteRef = useRef(preDebateLikertComplete);
  useEffect(() => {
    preDebateLikertCompleteRef.current = preDebateLikertComplete;
  }, [preDebateLikertComplete]);

  const armChatTimer = useCallback(() => {
    if (!API_BASE_URL || !currentUserProlificID || startChatInFlightRef.current) return;
    startChatInFlightRef.current = true;
    fetch(`${API_BASE_URL}/startChat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'prolific-id': currentUserProlificID,
      },
      body: JSON.stringify({ currentRoundIndex: currentRoundRef.current }),
    })
      .then((res) => res.json().then((payload) => ({ ok: res.ok, data: payload })))
      .then(({ ok, data: chatData }) => {
        startChatInFlightRef.current = false;
        if (chatData.chatEnded && CHAT_FIRST && currentRoundRef.current === 0) {
          if (chatRepairAttemptsRef.current >= 2) {
            console.error('[Game] startChat: repair attempts exhausted');
            return;
          }
          chatRepairAttemptsRef.current += 1;
          console.warn('[Game] startChat: server already in round phase before debate — repairing');
          return fetch(`${API_BASE_URL}/postTutorialFeedback/skipAndInitGameRound`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'prolific-id': currentUserProlificID,
            },
          })
            .then(() => {
              startChatInFlightRef.current = false;
              armChatTimer();
            })
            .catch(() => {
              startChatInFlightRef.current = false;
            });
        }
        if (!ok) return;
        if (chatData.chatStartTime != null) setPhaseStartTime(chatData.chatStartTime);
        if (chatData.timeRemaining != null) setSecondsLeft(chatData.timeRemaining);
      })
      .catch(() => {
        startChatInFlightRef.current = false;
      });
  }, [currentUserProlificID]);

  // Ensure chat-first game state is initialized when entering the main game.
  useEffect(() => {
    if (!API_BASE_URL || !currentUserProlificID || !CHAT_FIRST) return;

    fetch(`${API_BASE_URL}/postTutorialFeedback/skipAndInitGameRound`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'prolific-id': currentUserProlificID,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          console.log('[Game] Chat-first init confirmed:', data);
        }
      })
      .catch((err) => {
        console.warn('[Game] Chat-first init request failed (polling will retry):', err);
      });
  }, [currentUserProlificID]);

  const chatNumber = isChatPeriod ? 1 : undefined;
  const roundNumber = currentRound + 1;

  usePhaseTracking('GAME', currentUserProlificID, {
    roundNumber: isChatPeriod ? undefined : roundNumber,
    chatNumber: chatNumber,
    isChatPeriod: isChatPeriod,
    currentRound: currentRound,
  });

  useEffect(() => {
    if (gameOver) {
      const queryParams = new URLSearchParams(location.search);
      // Surveys come before investment results are revealed.
      const newUrl = '/post-game-questions?' + queryParams.toString();
      navigate(newUrl);
    }
  }, [gameOver, navigate, location.search]);

  // SIMPLIFIED: Poll backend frequently to get all game state (round, phase, time remaining)
  // All state comes from backend - no local calculations
  useEffect(() => {
    if (!API_BASE_URL || !currentUserProlificID) return;
    
    let isActive = true;
    let lastTimeRemaining: number | null = null;
    let lastRoundPhase: string = '';
    let expirationTriggered = false;
    let stuckAtZeroCount = 0; // Track how many consecutive polls we've been at 0
    let lastZeroTime: number | null = null; // Track when we first hit 0
    
    const pollGameState = async () => {
      if (!isActive) return;
      
      // During round-end UI we may skip timer/sync updates, but NEVER skip the heartbeat —
      // stopping checkPlayerStatus caused false mutual disconnects (both partners flagged).
      const shouldSkipGameStateSync =
        isProcessingRoundEndRef.current && lastTimeRemaining !== null && lastTimeRemaining > 0;

      if (shouldSkipGameStateSync) {
        console.log('🔄 Round-end processing: heartbeat only (skipping timer sync)');
      }

      try {
        const res = await fetch(`${API_BASE_URL}/checkPlayerStatus`, {
          method: 'POST',
          headers: { 'prolific-id': currentUserProlificID }
        });
        
        if (!res.ok) {
          console.error('❌ Failed to poll game state');
          return;
        }
        
        const data = await res.json();
        if (Object.prototype.hasOwnProperty.call(data, 'usdEarnings')) {
          setUsdEarnings((data as { usdEarnings?: UsdEarningsFromServer | null }).usdEarnings ?? null);
        }

        const payload = data as { debateTopic?: string };
        if (typeof payload.debateTopic === 'string' && payload.debateTopic.trim()) {
          setDebateTopic(payload.debateTopic.trim());
        }

        if (
          data.gameStatus !== 'completed' &&
          (data.partnerDisconnected === true || data.gameStatus === 'player disconnected')
        ) {
          if (debateLikertTimingRef.current) {
            console.log('⚠️ Disconnect flag ignored during debate Likert popup');
          } else if (isProcessingRoundEndRef.current) {
            console.log('⚠️ Disconnect flag ignored during round results / transition');
          } else {
            console.log('⚠️ Partner disconnected detected via checkPlayerStatus heartbeat');
            setPartnerDisconnected(true);
            setDisconnectReward(playerTotalEarningsRef.current);
            return;
          }
        }

        if (shouldSkipGameStateSync) {
          return;
        }

        console.log('📡 Polling game state:', {
          round: data.currentRoundIndex,
          phase: data.phaseType,
          timeRemaining: data.timeRemaining,
          roundStartTime: data.roundStartTime ? new Date(data.roundStartTime).toISOString() : null
        });
        
        // Sync round from backend (or catch up if we're behind so the other party sees Round Results popup)
        if (data.currentRoundIndex !== null && data.currentRoundIndex !== undefined) {
          const backendRound = data.currentRoundIndex;
          const ourRound = currentRoundRef.current;
          if (backendRound > ourRound) {
            // We're behind: other player advanced. Fetch our round results and show Round Results popup (other party fix).
            if (!catchUpFetchInProgressRef.current && roundResultsPopupShownForRef.current !== backendRound) {
              catchUpFetchInProgressRef.current = true;
              const roundToFetch = ourRound;
              fetch(`${API_BASE_URL}/nextRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'prolific-id': currentUserProlificID },
                body: JSON.stringify({ currentRoundIndex: roundToFetch }),
              })
                .then((res) => res.json().then((payload) => ({ ok: res.ok, data: payload })))
                .then(({ ok, data: nextData }) => {
                  catchUpFetchInProgressRef.current = false;
                  if (!ok || nextData.notReady) return;
                  const serverRound = nextData.currentRoundIndex ?? nextData.newRoundIndex;
                  const hasRoundResults =
                    nextData.playerEarnings != null && nextData.partnerEarnings != null;
                  const gameEnded =
                    nextData.gameCompleted ||
                    nextData.gameStatus === 'completed' ||
                    (typeof serverRound === 'number' && serverRound >= TOTAL_GAME_ROUNDS);

                  if (gameEnded && hasRoundResults) {
                    setPartnerDisconnected(false);
                    setPlayerLastRoundEarning(nextData.playerEarnings);
                    setPartnerLastRoundEarning(nextData.partnerEarnings);
                    setPlayerTotalEarnings((p) => p + nextData.playerEarnings);
                    setLastRoundEarnings(nextData.playerEarnings);
                    setLastPartnerEarnings(nextData.partnerEarnings);
                    roundResultsPopupShownForRef.current = serverRound;
                    setRoundPopupIsFinalGameRound(true);
                    setShowRoundPopup(true);
                    setTimeout(() => {
                      setShowRoundPopup(false);
                      setRoundPopupIsFinalGameRound(false);
                      setGameOver(true);
                      currentRoundRef.current = serverRound;
                      setCurrentRound(serverRound);
                    }, roundResultsPopupDurationMs(roundToFetch));
                    return;
                  }

                  if (gameEnded && !hasRoundResults) {
                    setPartnerDisconnected(false);
                    setGameOver(true);
                    currentRoundRef.current = serverRound;
                    setCurrentRound(serverRound);
                    roundResultsPopupShownForRef.current = serverRound;
                    return;
                  }
                  setPlayerLastRoundEarning(nextData.playerEarnings);
                  setPartnerLastRoundEarning(nextData.partnerEarnings);
                  setPlayerTotalEarnings((p) => p + nextData.playerEarnings);
                  setLastRoundEarnings(nextData.playerEarnings);
                  setLastPartnerEarnings(nextData.partnerEarnings);
                  if (nextData.initialTokens != null) setInitialTokens(nextData.initialTokens);
                  currentRoundRef.current = serverRound;
                  setCurrentRound(serverRound);
                  setIsChatPeriod(
                    isDebateChatPhase(
                      nextData.phaseType,
                      serverRound,
                      debatePhaseTimingFromPayload(nextData)
                    )
                  );
                  if (nextData.timeRemaining != null) setSecondsLeft(nextData.timeRemaining);
                  if (nextData.phaseType === 'chat' && nextData.chatStartTime) setPhaseStartTime(nextData.chatStartTime);
                  else if (nextData.phaseType === 'round' && nextData.roundStartTime) setPhaseStartTime(nextData.roundStartTime);
                  roundResultsPopupShownForRef.current = serverRound;
                  setRoundPopupIsFinalGameRound(false);
                  setShowRoundPopup(true);

                  // Close Round Results popup after a short delay.
                  setTimeout(() => {
                    setShowRoundPopup(false);
                    setRoundPopupIsFinalGameRound(false);
                  }, roundResultsPopupDurationMs(roundToFetch));
                })
                .catch(() => { catchUpFetchInProgressRef.current = false; });
            }
          } else if (backendRound !== ourRound) {
            console.log(`🔄 Round sync: ${ourRound} → ${backendRound}`);
            currentRoundRef.current = backendRound;
            setCurrentRound(backendRound);
            lastTimeRemaining = null;
            lastRoundPhase = '';
            expirationTriggered = false;
          }
        }
        
        // Sync phase from backend - CRITICAL for chat-to-round transitions
        const roundIdx = data.currentRoundIndex ?? currentRoundRef.current ?? 0;
        const phaseTiming = debatePhaseTimingFromPayload(data);
        let isChat = isDebateChatPhase(data.phaseType, roundIdx, phaseTiming);
        // Never sync out of debate before the pre-debate Likert is submitted.
        if (CHAT_FIRST && roundIdx === 0 && !preDebateLikertCompleteRef.current) {
          isChat = true;
        }
        const currentRoundPhase = `${roundIdx}-${data.phaseType}`;
        if (currentRoundPhase !== lastRoundPhase && lastRoundPhase !== '') {
          // Phase or round changed - reset tracking
          console.log(`🔄 Phase/round changed: ${lastRoundPhase} → ${currentRoundPhase}`);
          lastTimeRemaining = null;
          expirationTriggered = false;
          
          // CRITICAL: If phase changed from chat to round, clear processing flag
          // This handles cases where backend transitioned but frontend was stuck
          if (lastRoundPhase.includes('chat') && currentRoundPhase.includes('round')) {
            console.log(`✅ Backend transitioned from chat to round - clearing processing flag`);
            setIsProcessingRoundEnd(false);
            setRoundEndMessage(null);
          }
        }
        
        // CRITICAL: Always sync phase from backend on every poll
        // Backend is the single source of truth; never allow frontend to stay in stale phase
        if (isChat !== isChatPeriodRef.current) {
          console.log(`💬 Phase sync (polling): ${isChatPeriodRef.current ? 'chat' : 'round'} → ${isChat ? 'chat' : 'round'}`);
          expirationTriggered = false;
        }
        setIsChatPeriod(isChat);

        // Arm chat timer only after the pre-debate Likert is complete.
        if (
          isChat &&
          (data.chatStartTime === null || data.chatStartTime === undefined) &&
          preDebateLikertCompleteRef.current
        ) {
          armChatTimer();
        }
        
        // If backend says we're in round but frontend was in chat, clear processing
        if (!isChat && isChatPeriodRef.current) {
          console.log(`✅ Backend phase is 'round' but frontend was in 'chat' - clearing processing flag`);
          console.log(`   This indicates backend transitioned successfully - syncing frontend state`);
          setIsProcessingRoundEnd(false);
          setRoundEndMessage(null);
          
          // Also sync round and time if available
          if (data.currentRoundIndex !== null && data.currentRoundIndex !== undefined) {
            currentRoundRef.current = data.currentRoundIndex;
            setCurrentRound(data.currentRoundIndex);
          }
          if (data.timeRemaining !== null && data.timeRemaining !== undefined) {
            setSecondsLeft(data.timeRemaining);
          }
        }
        
        
        // CRITICAL: Use backend-provided time remaining — but never apply investment-round
        // countdown while the debate UI is active (chat-first can briefly report phase=round).
        const syncedSeconds = resolveSyncedTimerSeconds(isChat, data);
        const effectiveTimeRemaining =
          syncedSeconds ?? (isChat ? CHAT_DURATION : data.timeRemaining ?? null);

        if (effectiveTimeRemaining !== null && effectiveTimeRemaining !== undefined) {
          setSecondsLeft(effectiveTimeRemaining);
          
          if (effectiveTimeRemaining !== lastTimeRemaining) {
            const phaseLabel = isChat ? 'Chat' : 'Round';
            console.log(`⏰ ${phaseLabel} timer (synced): ${effectiveTimeRemaining}s remaining (${phaseLabel} ${data.currentRoundIndex + 1})`);
          }
          
          if (effectiveTimeRemaining <= 0) {
            if (lastZeroTime === null) {
              lastZeroTime = Date.now();
              stuckAtZeroCount = 1;
            } else {
              stuckAtZeroCount++;
              const timeStuck = Date.now() - lastZeroTime;
              
              // If stuck at 0 for more than 1 second in chat, just wait for backend transition
              if (timeStuck > 1000 && isChat && data.chatStartTime) {
                console.log(`⏳ Chat timer at 0 for ${timeStuck}ms - waiting for backend transition`);
              }
            }
          } else {
            // Timer is running - reset stuck tracking
            lastZeroTime = null;
            stuckAtZeroCount = 0;
          }
          
          // NOTE: Phase sync already happens at line 177-202, no need to duplicate here
          
          // Detect timer expiration: timeRemaining is 0 and we haven't triggered yet
          // CRITICAL: Don't check isProcessingRoundEnd here - let handler check it internally
          // This avoids race conditions where state hasn't updated yet
          if (effectiveTimeRemaining <= 0 && 
              !expirationTriggered &&
              data.phaseType) {
            expirationTriggered = true;
            const phaseLabel = isChat ? 'Chat' : 'Round';
            console.log(`⏰ ${phaseLabel} timer expired (synced): ${phaseLabel} ${data.currentRoundIndex + 1}`);
            console.log(`   Phase from backend: ${data.phaseType}, backend isChat: ${isChat}, frontend isChatPeriod: ${isChatPeriodRef.current}`);
            console.log(`   effectiveTimeRemaining: ${effectiveTimeRemaining}, expirationTriggered: ${expirationTriggered}, isProcessingRoundEnd: ${isProcessingRoundEndRef.current}`);
            
            if (isChat && data.chatStartTime) {
              console.log(`✅ Chat expired - syncing via nextRound (server may already have advanced)`);
              handleChatPeriodEndRef.current?.();
            } else if (!isChat && data.phaseType === 'round' && handleRoundEndRef.current) {
              // For rounds, also trigger immediately
              const handler = handleRoundEndRef.current;
              console.log(`🚀 Triggering handleRoundEnd immediately (backend: phase=round, timeRemaining=0)`);
              handler();
            } else {
              // Phase mismatch or no handler - reset flag to allow retry
              console.log(`⚠️  Phase mismatch or no handler: backend=${data.phaseType}, backend isChat=${isChat}, handler=${!!(isChat ? handleChatPeriodEndRef.current : handleRoundEndRef.current)}`);
              expirationTriggered = false;
            }
          } else if (effectiveTimeRemaining > 0) {
            expirationTriggered = false;
            lastZeroTime = null;
            stuckAtZeroCount = 0;
          } else if (effectiveTimeRemaining <= 0 && expirationTriggered && isProcessingRoundEndRef.current) {
            console.log(`⏳ Timer expired, transition in progress...`);
          } else if (effectiveTimeRemaining <= 0 && !expirationTriggered && isProcessingRoundEndRef.current) {
            console.log(`⏳ Timer expired but transition already in progress`);
          }
          
          lastTimeRemaining = effectiveTimeRemaining;
          lastRoundPhase = currentRoundPhase;
        } else {
          // No time remaining data - might be in transition
          const phaseLabel = isChat ? 'Chat' : 'Round';
          console.log(`⚠️ No timeRemaining from backend - phaseType: ${data.phaseType}, roundStartTime: ${data.roundStartTime ? new Date(data.roundStartTime).toISOString() : 'null'}, chatStartTime: ${data.chatStartTime ? new Date(data.chatStartTime).toISOString() : 'null'}, currentRoundIndex: ${data.currentRoundIndex}`);
          
          // If we're in a phase but don't have timeRemaining, log error
          if (data.phaseType === 'round' && data.currentRoundIndex !== null && data.currentRoundIndex !== undefined) {
            console.error(`❌ CRITICAL: Round ${data.currentRoundIndex} active but roundStartTime is null! Game may not be initialized correctly.`);
          } else if (isChat) {
            console.error(`❌ CRITICAL: Debate chat active but chatStartTime is null! Complete the pre-debate question or wait for sync.`);
          }
          
          // Show default time but don't update if we already have a valid time
          const defaultTime = isChat ? CHAT_DURATION : ROUND_DURATION;
          if (lastTimeRemaining === null || lastTimeRemaining === defaultTime) {
            setSecondsLeft(defaultTime);
          }
          lastTimeRemaining = null;
        }
        
        // Update initial tokens
        if (data.initialTokens !== null && data.initialTokens !== undefined) {
          setInitialTokens(data.initialTokens);
        }
        
        // Update phase start time for reference (but we use timeRemaining from backend)
        if (data.chatStartTime) {
          setPhaseStartTime(data.chatStartTime);
        } else if (data.phaseType === 'round' && data.roundStartTime) {
          setPhaseStartTime(data.roundStartTime);
        }
        
      } catch (err) {
        console.error('❌ Error polling game state:', err);
      }
    };
    
    // Poll immediately, then every 500ms
    pollGameState();
    const interval = setInterval(pollGameState, 500);
    
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [currentUserProlificID, armChatTimer]); // Only depend on prolificID - don't restart on state changes

  useEffect(() => {
    if (isChatPeriod && !preDebateLikertComplete && debateLikertTiming !== 'post') {
      setDebateLikertTiming('pre');
    }
  }, [isChatPeriod, preDebateLikertComplete, debateLikertTiming]);

  // Safety net for the post-debate question. The backend's checkPlayerStatus poll
  // auto-advances chat → round the instant the chat timer hits zero, so the frontend
  // often syncs isChatPeriod to false (via polling) before its own timer-expiry handler
  // (handleChatPeriodEnd) can run — which would silently skip the post-debate Likert.
  // Detect the chat → round transition here and ask the post question once. This complements
  // handleChatPeriodEnd: if that path already set timing to 'post', this is a no-op.
  useEffect(() => {
    const wasChat = prevIsChatPeriodRef.current;
    prevIsChatPeriodRef.current = isChatPeriod;
    if (
      wasChat &&
      !isChatPeriod &&
      preDebateLikertComplete &&
      !postDebateLikertComplete &&
      debateLikertTimingRef.current !== 'post'
    ) {
      console.log('🛟 Chat ended (chat → round) without post-debate Likert — showing post question');
      setDebateLikertTiming('post');
    }
  }, [isChatPeriod, preDebateLikertComplete, postDebateLikertComplete]);

  const submitDebateLikert = useCallback(
    async (timing: DebateLikertTiming, rating: number) => {
      if (!API_BASE_URL || !currentUserProlificID) return;
      setDebateLikertSubmitting(true);
      try {
        const res = await fetch(`${API_BASE_URL}/debateLikert`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': currentUserProlificID,
          },
          body: JSON.stringify({
            timing,
            rating,
            questionText: getDebateLikertQuestion(debateTopic),
            debateTopicTitle: debateTopic,
          }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(parseApiErrorBody(errBody, res.status));
        }
        if (timing === 'pre') {
          setPreDebateLikertComplete(true);
          setDebateLikertTiming(null);
          armChatTimer();
        } else {
          setPostDebateLikertComplete(true);
          setDebateLikertTiming(null);
          if (chatEndQueuedRef.current) {
            chatEndQueuedRef.current = false;
            await runChatPeriodEndRef.current?.();
          }
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not save your response.';
        console.error('Debate Likert submit failed:', msg, e);
        window.alert(`${msg}\n\nPlease try again.`);
      } finally {
        setDebateLikertSubmitting(false);
      }
    },
    [currentUserProlificID, armChatTimer, debateTopic]
  );

  const runChatPeriodEnd = async () => {
    // CRITICAL: Prevent multiple simultaneous calls, but allow retry if stuck
    if (isProcessingRoundEnd) {
      // Check if we've been processing for too long (more than 5 seconds)
      // This indicates the handler might be stuck
      const processingTime = Date.now() - (window as any).__chatProcessingStartTime || 0;
      if (processingTime > 5000) {
        console.log(`⚠️  handleChatPeriodEnd: Processing for ${processingTime}ms - allowing retry`);
        // Reset processing flag to allow retry
        setIsProcessingRoundEnd(false);
      } else {
        console.log("handleChatPeriodEnd: Already processing, skipping");
        return;
      }
    }
    
    // Track when processing started
    (window as any).__chatProcessingStartTime = Date.now();
    
    console.log(`💬 handleChatPeriodEnd: Starting`);
    console.log(`   Current round ref: ${currentRoundRef.current}`);
    console.log(`   Current phase state: ${isChatPeriod ? 'chat' : 'round'}`);
    console.log(`   Processing flag: ${isProcessingRoundEnd}`);
    
    setIsProcessingRoundEnd(true);
    setRoundEndMessage("Finishing the investment round...");

    // CRITICAL: First check with backend what the current state actually is
    // This prevents race conditions where polling already updated our state
    // but we're about to send a transition request with the new round index
    try {
      console.log(`🔍 Checking backend state before chat transition...`);
      const statusRes = await fetch(`${API_BASE_URL}/checkPlayerStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'prolific-id': currentUserProlificID },
        body: JSON.stringify({}),
      });
      
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        console.log(`📊 Backend state check:`, {
          round: statusData.currentRoundIndex,
          phase: statusData.phaseType,
          timeRemaining: statusData.timeRemaining
        });
        
        // CRITICAL: If backend is already in 'round' phase, the transition already happened!
        // Another player (or a previous call) already transitioned. Just sync and return.
        if (statusData.phaseType === 'round') {
          console.log(`✅ Backend already in 'round' phase - transition already happened by other player`);
          console.log(`   Syncing frontend state to round ${statusData.currentRoundIndex}`);
          
          // Sync state with backend
          currentRoundRef.current = statusData.currentRoundIndex;
          setCurrentRound(statusData.currentRoundIndex);
          setIsChatPeriod(false);
          if (statusData.timeRemaining !== null && statusData.timeRemaining !== undefined) {
            setSecondsLeft(statusData.timeRemaining);
          }
          if (statusData.roundStartTime) {
            setPhaseStartTime(statusData.roundStartTime);
          }
          
          // Clear processing state
          setIsProcessingRoundEnd(false);
          setRoundEndMessage(null);
          (window as any).__chatProcessingStartTime = null;
          
          console.log(`✅ Synced to round ${statusData.currentRoundIndex}, no transition needed`);
          return; // Exit early - no need to call /nextRound
        }
        
        // If backend is in chat, we need to use the round index the backend has
        // (in case polling already updated our ref to the wrong value)
        if (isDebateChatPhase(
          statusData.phaseType,
          statusData.currentRoundIndex ?? 0,
          debatePhaseTimingFromPayload(statusData)
        )) {
          console.log(`💬 Backend confirms debate chat phase - using backend round index: ${statusData.currentRoundIndex}`);
          // Store the correct round index to use for transition
          (window as any).__chatTransitionRoundIndex = statusData.currentRoundIndex;
        }
      }
    } catch (err) {
      console.warn(`⚠️  Could not check backend state, proceeding with current ref: ${currentRoundRef.current}`);
    }
    
    // Use the verified round index from backend check, or fall back to ref
    const transitionRoundIndex = (window as any).__chatTransitionRoundIndex ?? currentRoundRef.current;

    // CRITICAL: Poll until we get a valid transition response
    const poll = async (attemptCount = 0) => {
      try {
        // Safety: prevent infinite loops
        if (attemptCount > 30) {
          console.error(`❌ Chat end polling exceeded max attempts (30)`);
          setIsProcessingRoundEnd(false);
          setRoundEndMessage(null);
          (window as any).__chatProcessingStartTime = null;
          (window as any).__chatTransitionRoundIndex = null;
          // Force sync with backend state as last resort
          console.log(`🔄 Forcing sync with backend state as fallback`);
          return;
        }
        
        console.log(`📡 Chat end polling /nextRound (attempt ${attemptCount + 1}) with roundIndex=${transitionRoundIndex}`);
        const res = await fetch(`${API_BASE_URL}/nextRound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'prolific-id': currentUserProlificID },
          body: JSON.stringify({ currentRoundIndex: transitionRoundIndex }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          if (errorData.notReady) {
            console.log(`⏳ Chat end not ready (attempt ${attemptCount + 1}), polling again...`);
            setTimeout(() => poll(attemptCount + 1), 1000);
            return;
          }
          console.error(`❌ Chat end error: ${res.status}`, errorData);
          setTimeout(() => poll(attemptCount + 1), 1000);
          return;
        }

        const data = await res.json();
        console.log(`📨 Chat end response:`, data);

        // CRITICAL: Verify we got a valid response with phase transition to 'round'
        if (!data.phaseType) {
          console.error(`❌ Invalid response: missing phaseType`);
          setTimeout(() => poll(attemptCount + 1), 1000);
          return;
        }

        // If still in chat, keep polling (might be a timing issue)
        // But also check if we've been polling too long - might indicate backend issue
        if (data.phaseType === 'chat') {
          if (attemptCount > 10) {
            console.error(`❌ Still in chat phase after ${attemptCount} attempts - backend may not be transitioning`);
            console.error(`   Current state: round=${data.currentRoundIndex}, phase=${data.phaseType}, chatStartTime=${data.chatStartTime}`);
          }
          // If the backend told us exactly how long chat still has to run, wait that long
          // (plus a small buffer) before retrying — avoids hammering the server.
          if (data.chatNotExpiredYet && data.timeRemaining != null && data.timeRemaining > 1) {
            const waitMs = (data.timeRemaining + 1) * 1000;
            console.log(`⛔ Chat not expired yet — ${data.timeRemaining}s remaining. Waiting ${waitMs}ms before retry.`);
            setTimeout(() => poll(attemptCount + 1), waitMs);
          } else {
            console.log(`⏳ Still in chat phase (attempt ${attemptCount + 1}), waiting for transition...`);
            setTimeout(() => poll(attemptCount + 1), 500);
          }
          return;
        }

        // Sync with server's round index and timing - ALL FROM BACKEND
        const serverRound = data.currentRoundIndex ?? data.newRoundIndex;
        const phase = data.phaseType;
        
        console.log(`✅ Chat complete! SYNCING: Sent roundIndex=${transitionRoundIndex} → Server returned ${serverRound}`);
        console.log(`   Phase: ${phase} (should be 'round')`);
        console.log(`   Time Remaining: ${data.timeRemaining ?? 'N/A'}s`);
        console.log(`   Round Start Time: ${data.roundStartTime ? new Date(data.roundStartTime).toISOString() : 'null'}`);
        console.log(`   Chat Start Time: ${data.chatStartTime ? new Date(data.chatStartTime).toISOString() : 'null'}`);
        
        // SAFETY: Verify server round is valid
        if (typeof serverRound !== 'number' || serverRound < 0) {
          console.error(`❌ INVALID SERVER ROUND: ${serverRound}`);
          setTimeout(() => poll(attemptCount + 1), 1000);
          return;
        }

        // CRITICAL: Must transition to 'round' phase, not stay in 'chat'
        if (phase !== 'round') {
          console.error(`❌ INVALID PHASE: Expected 'round', got '${phase}'`);
          setTimeout(() => poll(attemptCount + 1), 1000);
          return;
        }
        
        // CRITICAL: Verify chatStartTime is null (chat should be cleared)
        if (data.chatStartTime !== null && data.chatStartTime !== undefined) {
          console.error(`❌ WARNING: chatStartTime should be null after chat ends, got: ${data.chatStartTime}`);
        }
        
        // CRITICAL: Update ALL state atomically to prevent UI glitches
        // Update phase FIRST to prevent showing "Chat Session" in UI
        setIsChatPeriod(false);
        
        // Update ref and state to exact server value
        currentRoundRef.current = serverRound;
        setCurrentRound(serverRound);
        
        // Use backend-provided time remaining
        if (data.timeRemaining !== null && data.timeRemaining !== undefined) {
          setSecondsLeft(data.timeRemaining);
        } else {
          // Fallback to full round duration if timeRemaining not provided
          console.warn(`⚠️  No timeRemaining from backend, using default ${ROUND_DURATION}s`);
          setSecondsLeft(ROUND_DURATION);
        }
        
        // Update phase start time for reference (avoid clearing to undefined — breaks round-0 gate / remount)
        if (data.roundStartTime != null) {
          setPhaseStartTime(data.roundStartTime);
        }
        
        // Update initial tokens for new round
        if (data.initialTokens !== null && data.initialTokens !== undefined) {
          setInitialTokens(data.initialTokens);
          console.log(`💰 Initial tokens for round ${serverRound}: ${data.initialTokens}`);
        }
        
        // Clear processing flag and tracking
        setIsProcessingRoundEnd(false);
        setRoundEndMessage(null);
        (window as any).__chatProcessingStartTime = null;
        (window as any).__chatTransitionRoundIndex = null;
        
        console.log(`✅ CHAT SYNC COMPLETE: Round ${serverRound}, Phase ${phase}, Time ${data.timeRemaining}s`);
        console.log(`   State updated: isChatPeriod=false, currentRound=${serverRound}`);
      } catch (err) {
        console.error("❌ Error in chat poll:", err);
        setTimeout(() => poll(attemptCount + 1), 1000);
      }
    };

    poll();
  };

  const handleChatPeriodEnd = async () => {
    if (!postDebateLikertComplete) {
      chatEndQueuedRef.current = true;
      setDebateLikertTiming('post');
      return;
    }
    await runChatPeriodEnd();
  };

  useEffect(() => {
    runChatPeriodEndRef.current = runChatPeriodEnd;
  });

  const handleRoundEnd = async () => {
    if (isProcessingRoundEnd) {
      console.log("⚠️  handleRoundEnd: Already processing, skipping duplicate call");
      return;
    }
    
    // Bonus popup removed in the single-round chat-first game.
    // CRITICAL: Don't process round end if we're on round 0 and just starting
    // This prevents showing results popup when game first starts
    if (currentRoundRef.current === 0 && phaseStartTime == null) {
      console.log("⚠️  handleRoundEnd: Round 0 and no phaseStartTime - game not initialized yet, skipping");
      return;
    }

    const transitionRoundIndex = (window as any).__roundTransitionRoundIndex ?? currentRoundRef.current;
    console.log(`🎮 handleRoundEnd: Starting for round ${currentRoundRef.current} (transitionRoundIndex=${transitionRoundIndex})`);
    
    // CRITICAL: First check backend state to prevent race conditions
    // This prevents firing immediately after a chat-to-round transition
    try {
      console.log(`🔍 Checking backend state before round end...`);
      const statusRes = await fetch(`${API_BASE_URL}/checkPlayerStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'prolific-id': currentUserProlificID },
        body: JSON.stringify({}),
      });
      
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        console.log(`📊 Backend state check:`, {
          round: statusData.currentRoundIndex,
          phase: statusData.phaseType,
          timeRemaining: statusData.timeRemaining,
          transitionRoundIndex
        });
        
        // CRITICAL: Only skip if we're still on the SAME round AND backend has significant time left.
        // If the backend has already advanced (other player called nextRound), we must NOT skip -
        // we need to poll nextRound to get our round results (catch-up) and show the popup.
        const backendAlreadyAdvanced = statusData.currentRoundIndex != null && transitionRoundIndex != null && statusData.currentRoundIndex > transitionRoundIndex;
        if (!backendAlreadyAdvanced && statusData.timeRemaining != null && statusData.timeRemaining > 5) {
          console.log(`⚠️  handleRoundEnd: Backend still on round ${statusData.currentRoundIndex} with ${statusData.timeRemaining}s remaining - too early, skipping`);
          
          // Sync state with backend
          currentRoundRef.current = statusData.currentRoundIndex;
          setCurrentRound(statusData.currentRoundIndex);
          setIsChatPeriod(
            isDebateChatPhase(
              statusData.phaseType,
              statusData.currentRoundIndex ?? 0,
              debatePhaseTimingFromPayload(statusData)
            )
          );
          setSecondsLeft(statusData.timeRemaining);
          
          return; // Exit early - don't process round end
        }
        if (backendAlreadyAdvanced) {
          console.log(`📥 handleRoundEnd: Backend already at round ${statusData.currentRoundIndex} (we're ${transitionRoundIndex}) - will poll nextRound for catch-up`);
        }
        
        // CRITICAL: If backend is in chat phase — regardless of whether backendAlreadyAdvanced is
        // true or false — do NOT call /nextRound. nextRound(index=3) while phaseType=chat triggers
        // the chat→round transition immediately, skipping the debate entirely.
        // Return here; the polling catch-up will show round results and enter chat naturally.
        if (isDebateChatPhase(
          statusData.phaseType,
          statusData.currentRoundIndex ?? 0,
          debatePhaseTimingFromPayload(statusData)
        )) {
          console.log(`⚠️  handleRoundEnd: Backend is in debate chat — NOT calling nextRound (avoids skipping chat)`);
          console.log(`   round=${statusData.currentRoundIndex}, backendAlreadyAdvanced=${backendAlreadyAdvanced}`);
          currentRoundRef.current = statusData.currentRoundIndex;
          setCurrentRound(statusData.currentRoundIndex);
          setIsChatPeriod(true);
          if (statusData.timeRemaining != null) setSecondsLeft(statusData.timeRemaining);
          (window as any).__roundTransitionRoundIndex = null;
          return;
        }
        
        // Only reached when backend is in 'round' phase — safe to store for nextRound call
        (window as any).__roundTransitionRoundIndex = statusData.currentRoundIndex;
      }
    } catch (err) {
      console.warn(`⚠️  Could not check backend state, proceeding with current ref: ${currentRoundRef.current}`);
    }
    
    // Use verified round index (may have been set in try block) or keep transitionRoundIndex from above
    const roundIndexToSend = (window as any).__roundTransitionRoundIndex ?? transitionRoundIndex;
    
    setIsProcessingRoundEnd(true);
    setRoundEndMessage("Synchronizing...");

    let pollAttemptCount = 0;
    const poll = async () => {
      try {
        pollAttemptCount++;
        
        // Safety: prevent infinite loops
        if (pollAttemptCount > 60) {
          console.error(`❌ Round end polling exceeded max attempts (60) - stopping`);
          setIsProcessingRoundEnd(false);
          setRoundEndMessage(null);
          (window as any).__roundTransitionRoundIndex = null;
          return;
        }
        
        console.log(`📡 Polling /nextRound with roundIndex=${roundIndexToSend} (attempt ${pollAttemptCount})`);
        const res = await fetch(`${API_BASE_URL}/nextRound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'prolific-id': currentUserProlificID },
          body: JSON.stringify({ currentRoundIndex: roundIndexToSend }),
        });

        const data = await res.json();
        console.log(`📨 Response:`, data);

        const serverRoundEarly = data.currentRoundIndex ?? data.newRoundIndex;
        const hasRoundResultsPayload =
          data.playerEarnings != null && data.partnerEarnings != null;
        const gameEndedResponse =
          data.gameCompleted ||
          data.gameStatus === 'completed' ||
          (typeof serverRoundEarly === 'number' && serverRoundEarly >= TOTAL_GAME_ROUNDS);

        // Session already finalized (e.g. repeat request): no earnings in payload → go straight to completion.
        if (res.ok && gameEndedResponse && !hasRoundResultsPayload) {
          console.log('🎉 GAME COMPLETED (no round results in this response) — navigating to completion');
          setPartnerDisconnected(false);
          setGameOver(true);
          setIsProcessingRoundEnd(false);
          (window as any).__roundTransitionRoundIndex = null;
          return;
        }

        // CRITICAL: If the backend guard says chat has not expired yet, the frontend
        // somehow called /nextRound while the debate was still running.
        // Do NOT show a popup; instead set isChatPeriod=true and exit cleanly.
        // The regular polling will maintain the chat timer until chat actually expires.
        if (res.ok && data.chatNotExpiredYet) {
          console.warn(`⛔ handleRoundEnd poll: backend refused chat→round — chat not expired yet (${data.timeRemaining}s left). Entering chat mode.`);
          currentRoundRef.current = data.currentRoundIndex ?? currentRoundRef.current;
          setCurrentRound(currentRoundRef.current);
          setIsChatPeriod(true);
          if (data.timeRemaining != null) setSecondsLeft(data.timeRemaining);
          setIsProcessingRoundEnd(false);
          setRoundEndMessage(null);
          (window as any).__roundTransitionRoundIndex = null;
          return;
        }

        if (!res.ok || data.notReady) {
          // Check if round timer has expired - backend should auto-invest in this case
          if (data.roundTimerExpired) {
            console.log(`⏰ Round timer expired - backend should auto-invest, waiting for calculation...`);
          }
          
          // Check if we've been waiting too long (more than 10 seconds)
          // This might indicate both players are stuck waiting for each other
          const waitTime = pollAttemptCount * 1000; // Rough estimate
          if (waitTime > 10000) {
            console.log(`⚠️  Waiting for round results for ${waitTime}ms - might be stuck`);
            console.log(`   Error: ${data.error || 'notReady'}`);
            console.log(`   Round timer expired: ${data.roundTimerExpired || false}`);
            console.log(`   This could mean both players are waiting for investments`);
            console.log(`   Backend should auto-invest when timer expires - checking backend state...`);
          }
          console.log("⏳ Not ready, polling again in 1s");
          setTimeout(poll, 1000);
        } else {
          // CRITICAL: Get server round FIRST
          const serverRound = data.currentRoundIndex ?? data.newRoundIndex;
          console.log(`✅ Results ready! Server round: ${serverRound}, Sent roundIndex: ${roundIndexToSend}`);
          
          // Update initial tokens for new round
          if (data.initialTokens !== null && data.initialTokens !== undefined) {
            setInitialTokens(data.initialTokens);
            console.log(`💰 Initial tokens for round ${serverRound}: ${data.initialTokens}`);
          }
          
          // Update earnings
          setPlayerLastRoundEarning(data.playerEarnings);
          setPartnerLastRoundEarning(data.partnerEarnings);
          setPlayerTotalEarnings(prev => prev + data.playerEarnings);
          setLastRoundEarnings(data.playerEarnings);
          setLastPartnerEarnings(data.partnerEarnings);
          
          // CRITICAL: Don't show popup for round 0 (first round) - players should start fresh
          // Only show popup if we're advancing from a previous round (serverRound > 0)
          if (serverRound > 0) {
            roundResultsPopupShownForRef.current = serverRound;
            const completingGame =
              data.gameCompleted ||
              data.gameStatus === 'completed' ||
              (typeof serverRound === 'number' && serverRound >= TOTAL_GAME_ROUNDS);
            setRoundPopupIsFinalGameRound(completingGame);
            setShowRoundPopup(true);
            
            // Capture for setTimeout closure
            const serverRoundForTimeout = serverRound;
            const transitionRoundForTimeout = roundIndexToSend;
            const dataForTimeout = data;
            
            const popupCloseDelayMs = roundResultsPopupDurationMs(transitionRoundForTimeout);
            setTimeout(() => {
              console.log(`🎬 Popup complete, transitioning to round ${serverRoundForTimeout}`);
              setShowRoundPopup(false);
              setRoundPopupIsFinalGameRound(false);
              
              if (serverRoundForTimeout >= TOTAL_GAME_ROUNDS) {
                console.log("🎉 Game over!");
                setGameOver(true);
                setIsProcessingRoundEnd(false);
                (window as any).__roundTransitionRoundIndex = null;
              } else {
                // Sync to server round (normal advance or catch-up)
                const phase = dataForTimeout.phaseType;
                
                console.log(`🔄 SYNCING: Client ${currentRoundRef.current} → Server ${serverRoundForTimeout}`);
                console.log(`   Phase: ${phase}`);
                console.log(`   Time Remaining: ${dataForTimeout.timeRemaining ?? 'N/A'}s`);
                
                if (typeof serverRoundForTimeout !== 'number' || serverRoundForTimeout < 0) {
                  console.error(`❌ INVALID SERVER ROUND: ${serverRoundForTimeout}`);
                  setIsProcessingRoundEnd(false);
                  (window as any).__roundTransitionRoundIndex = null;
                  return;
                }
                
                currentRoundRef.current = serverRoundForTimeout;
                setCurrentRound(serverRoundForTimeout);
                setIsChatPeriod(
                  isDebateChatPhase(
                    phase,
                    serverRoundForTimeout,
                    debatePhaseTimingFromPayload(dataForTimeout)
                  )
                );
                if (dataForTimeout.timeRemaining != null && dataForTimeout.timeRemaining !== undefined) {
                  setSecondsLeft(dataForTimeout.timeRemaining);
                }
                setPhaseStartTime(phase === 'chat' ? dataForTimeout.chatStartTime : dataForTimeout.roundStartTime);
                setIsProcessingRoundEnd(false);
                setRoundEndMessage(null);
                (window as any).__roundTransitionRoundIndex = null;
                console.log(`✅ SYNC COMPLETE: Round ${serverRoundForTimeout}, Phase ${phase}`);
              }
            }, popupCloseDelayMs);
          } else {
            // Round 0 (first round) - no popup, just sync and start playing
            console.log(`🎮 Starting round 0 (first round) - no popup, starting fresh`);
            
            // Sync to server round and timing - ALL FROM BACKEND
            const phase = data.phaseType;
            
            // Update ref and state to exact server value
            currentRoundRef.current = serverRound;
            setCurrentRound(serverRound);
            
            // Update phase from backend
            setIsChatPeriod(
              isDebateChatPhase(phase, serverRound, debatePhaseTimingFromPayload(data))
            );
            
            // Use backend-provided time remaining
            if (data.timeRemaining !== null && data.timeRemaining !== undefined) {
              setSecondsLeft(data.timeRemaining);
            }
            
            // Update phase start time for reference
            setPhaseStartTime(phase === 'chat' ? data.chatStartTime : data.roundStartTime);
            
            // Clear processing flag
            setIsProcessingRoundEnd(false);
            setRoundEndMessage(null);
            (window as any).__roundTransitionRoundIndex = null;
            
            console.log(`✅ First round initialized: Round ${serverRound + 1}, Phase ${phase}, Time ${data.timeRemaining}s`);
          }
        }
      } catch (err) {
        console.error("❌ Error in poll:", err);
        setTimeout(poll, 1000);
      }
    };

    poll();
  };


  useEffect(() => {
    if (!API_BASE_URL || !currentUserProlificID || isChatPeriod) return;

    const wsProtocol = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
    const wsBase = API_BASE_URL.replace(/^https?/, wsProtocol);
    const wsUrl = `${wsBase}/messages?prolificID=${encodeURIComponent(currentUserProlificID)}`;

    const ws = new window.WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('Disconnection WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'player_disconnected') {
          console.log('Partner disconnected detected via WebSocket');
          setPartnerDisconnected(true);
          setDisconnectReward(playerTotalEarningsRef.current); 
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      // If WebSocket closes unexpectedly, polling will handle disconnection detection
      if (event.code !== 1000) {
        console.log('WebSocket closed unexpectedly, will rely on polling for disconnection detection');
      }
    };
    
    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [currentUserProlificID, isChatPeriod]);

  // Keep ref in sync with playerTotalEarnings for disconnection polling
  useEffect(() => {
    playerTotalEarningsRef.current = playerTotalEarnings;
  }, [playerTotalEarnings]);


  useEffect(() => {
    // CRITICAL: Stop polling if game is over or partner already disconnected
    if (gameOver || partnerDisconnected || !API_BASE_URL || !currentUserProlificID) {
      console.log(`🛑 Disconnection polling stopped: gameOver=${gameOver}, partnerDisconnected=${partnerDisconnected}`);
      return;
    }
    
    console.log('👀 Starting disconnection polling...');
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/isGroupDisconnected`, {
          cache: 'no-store',
          headers: { 'prolific-id': currentUserProlificID }
        });
        
        if (!res.ok) {
          console.error('Failed to check disconnection status:', res.status);
          return;
        }
        
        const data = await res.json();
        console.log(`🔍 Disconnection check: isDisconnected=${data.isDisconnected}, gameStatus=${data.gameStatus}`);
        
        // Game completed on server: never treat as partner disconnect.
        // Do NOT set gameOver here — that would navigate to congratulations before the
        // Round Results popup for the final investment round finishes (both players rely
        // on handleRoundEnd / catch-up timeouts to call setGameOver after the modal).
        if (data.gameStatus === 'completed') {
          console.log('✅ GAME COMPLETED (server) — clearing disconnect flag; completion waits for final results popup');
          setPartnerDisconnected(false);
          return;
        }
        
        if (data.isDisconnected) {
          if (debateLikertTimingRef.current) {
            console.log('⚠️ Disconnect poll ignored during debate Likert popup');
          } else if (isProcessingRoundEndRef.current) {
            console.log('⚠️ Disconnect poll ignored during round results / transition');
          } else {
            console.log('⚠️ Partner disconnected detected via polling');
            setPartnerDisconnected(true);
            setDisconnectReward(playerTotalEarningsRef.current);
          }
        }
      } catch (e) {
        console.error('Error checking disconnection status:', e);
      }
    }, 1500);

    return () => {
      console.log('🛑 Disconnection polling cleanup');
      clearInterval(interval);
    };
  }, [gameOver, partnerDisconnected, currentUserProlificID]);

  // Store handlers in refs so polling effect can access them
  useEffect(() => {
    handleChatPeriodEndRef.current = handleChatPeriodEnd;
    handleRoundEndRef.current = handleRoundEnd;
  });

  // Bonus popup removed in the single-round chat-first game.

  // CRITICAL: If game is over, NEVER show disconnection screen - ALWAYS show completion
  if (gameOver) {
    console.log('🎉 Game over - navigating to completion screen');
    return null; // Navigation to completion screen will happen via useEffect
  }

  // Disconnect takes precedence over the "Initializing" gate so we never get stuck on the
  // synchronizing screen when the backend has already marked the partner as disconnected.
  if (partnerDisconnected && !debateLikertTiming) {
    console.log('⚠️ Partner disconnected - showing disconnection screen');
    const search = window.location.search;
    window.location.href = '/disconnection-notice' + (search || '');
    return null;
  }

  // Only block the main UI before the first round has ever been anchored from the server.
  // After mid-game chat ends, `currentRound` is > 0; if `phaseStartTime` is briefly missing or
  // cleared, we must NOT unmount — that remounts Chat, closes the WebSocket, and wipes history.
  if (phaseStartTime == null && !isChatPeriod && currentRound === 0 && !CHAT_FIRST) {
    console.log('⚠️ No phaseStartTime on round 0 - game not started yet (still in tutorial/post-tutorial feedback)');
    console.log('   Waiting for proper initialization from backend...');
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Initializing game...</h2>
        <p>Please wait while we synchronize with your partner.</p>
      </div>
    );
  }

  return (
    <>
      <RewardInfoBox />
      <div style={{
        textAlign: 'center',
        padding: '14px 20px',
        margin: '12px 15px 3px 15px',
        borderRadius: '12px',
        backgroundColor: isChatPeriod ? '#e3f2fd' : '#e8f5e9',
        color: '#2e7d32',
        border: `2px solid ${isChatPeriod ? '#2196f3' : '#4caf50'}`,
        fontWeight: '600',
        fontSize: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: (() => {
            const totalDuration = isChatPeriod ? CHAT_DURATION : ROUND_DURATION;
            const progress = ((totalDuration - secondsLeft) / totalDuration) * 100;
            return `linear-gradient(90deg, ${isChatPeriod ? '#2196f3' : '#4caf50'} ${progress}%, #e0e0e0 ${progress}%)`;
          })(),
        }}></div>
        {/* Timer + inline tab warning (shortened; replaces separate yellow banner) */}
        {isChatPeriod
          ? `Discussion (${secondsLeft} seconds remaining)`
          : `Investment Round (${secondsLeft} seconds remaining)`}{' '}
        <span
          style={{
            fontSize: '14px',
            fontWeight: 500,
            opacity: 0.92,
            color: isChatPeriod ? '#1565C0' : '#2e7d32',
          }}
        >
          {`(⚠️ don't switch tabs or minimize)`}
        </span>
        {/* Show waiting message when timer reaches 0 */}
        {secondsLeft === 0 && !isChatPeriod && (
          <div style={{ 
            marginTop: '8px', 
            fontSize: '14px',
            color: '#FF9800',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <span>⏳</span>
            <span>Waiting for the other player</span>
            <span className="loading-dots">
              <span style={{ animation: 'blink 1.4s infinite', animationDelay: '0s' }}>.</span>
              <span style={{ animation: 'blink 1.4s infinite', animationDelay: '0.2s' }}>.</span>
              <span style={{ animation: 'blink 1.4s infinite', animationDelay: '0.4s' }}>.</span>
            </span>
          </div>
        )}
        {typeof roundEndMessage === 'string' && !isProcessingRoundEnd && !isChatPeriod && (
          <div style={{ marginTop: '6px', fontSize: '14px' }}>{roundEndMessage}</div>
        )}
      </div>
      
      {/* Add CSS animation for loading dots */}
      <style>{`
        @keyframes blink {
          0%, 20% { opacity: 0.2; }
          40% { opacity: 1; }
          100% { opacity: 0.2; }
        }
      `}</style>
      
      <div className={`game-container${isChatPeriod ? ' game-container--chat-wide' : ''}`} style={isChatPeriod ? { paddingTop: 12 } : undefined}>
        <GamePlay
          roundNumber={currentRound}
          timeLeft={secondsLeft}
          prolificId={currentUserProlificID}
          disabled={isChatPeriod}
          playerTotalEarnings={playerTotalEarnings}
          performancePaymentUsd={
            typeof usdEarnings?.totalUsd === 'number' ? usdEarnings.totalUsd : null
          }
          playerEarningsHistory={playerEarningsHistory}
          isChatPeriod={isChatPeriod}
          initialTokens={initialTokens}
        />

        <ChatUI
          currentUserProlificID={currentUserProlificID}
          isLocked={!isChatPeriod || !preDebateLikertComplete}
          chatNumber={chatNumber}
          currentRound={currentRound}
          debateTopicTitle={debateTopic}
          isDebateLayout={isChatPeriod}
          onPartnerDisconnected={() => {
            setPartnerDisconnected(true);
            setDisconnectReward(playerTotalEarningsRef.current);
          }}
        />
      </div>

      {debateLikertTiming && (
        <DebateLikertPopup
          timing={debateLikertTiming}
          question={getDebateLikertQuestion(debateTopic)}
          isSubmitting={debateLikertSubmitting}
          onSubmit={(rating) => submitDebateLikert(debateLikertTiming, rating)}
        />
      )}

      {/* Popup after each round — final round defers coin reveal until after surveys */}
      {showRoundPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 32,
            boxShadow: '0 4px 32px rgba(0,0,0,0.12)',
            padding: '38px 48px',
            minWidth: 340,
            textAlign: 'center',
            maxWidth: '90vw',
          }}>
            {roundPopupIsFinalGameRound ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 18, color: '#1F2937' }}>
                  Investment Round Complete
                </div>
                <div style={{
                  background: '#EEF6FF',
                  borderRadius: 12,
                  padding: '18px 20px',
                  marginBottom: 12,
                  fontSize: 16,
                  color: '#1F2937',
                  lineHeight: 1.5,
                }}>
                  You will see the investment results on the next pages.
                </div>
                <div style={{ fontSize: 15, color: '#666', marginTop: 10 }}>
                  Taking you to the post-game questions...
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 18, color: '#1F2937' }}>Round Results</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 10 }}>
                  <div style={{ background: '#D4F4E0', borderRadius: 12, padding: '12px 24px', minWidth: 120 }}>
                    <div style={{ fontSize: 15, color: '#4B5563', marginBottom: 4 }}>You earned</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#374151' }}>
                      {lastRoundEarnings !== null ? lastRoundEarnings : '-'} <span role="img" aria-label="coin">💰</span>
                    </div>
                  </div>
                  <div style={{ background: '#FDE3C9', borderRadius: 12, padding: '12px 24px', minWidth: 120 }}>
                    <div style={{ fontSize: 15, color: '#4B5563', marginBottom: 4 }}>Partner earned</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#374151' }}>
                      {lastPartnerEarnings !== null ? lastPartnerEarnings : '-'} <span role="img" aria-label="coin">💰</span>
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 15, color: '#666', marginTop: 10 }}>
                  Wrapping up the investment round...
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </>
  );
}

export default Game;